import React, { useState, useEffect, useCallback } from 'react';
import { X, Mail, TrendingUp, CheckCircle } from 'lucide-react';
import { trackSubscribeAttempt, trackSubscribeSuccess } from '../utils/analytics';

const STORAGE_KEY = 'exit_popup_shown';

const ExitIntentPopup: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const show = useCallback(() => {
    if (sessionStorage.getItem(STORAGE_KEY)) return;
    sessionStorage.setItem(STORAGE_KEY, '1');
    setVisible(true);
  }, []);

  useEffect(() => {
    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 10) show();
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') show();
    };
    const timer = setTimeout(() => {
      document.addEventListener('mouseleave', handleMouseLeave);
      document.addEventListener('visibilitychange', handleVisibility);
    }, 8000);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [show]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    setError('');
    trackSubscribeAttempt('exit_popup');
    try {
      const res = await fetch('/.netlify/functions/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (res.ok) {
        trackSubscribeSuccess('exit_popup');
        setSubmitted(true);
      } else {
        const text = await res.text();
        setError(text || 'Something went wrong. Please try again.');
      }
    } catch {
      setError('Network error. Please try again.');
    }
    setLoading(false);
  };

  const dismiss = () => setVisible(false);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Subscribe for free market analysis"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={dismiss} />

      {/* Card */}
      <div className="relative w-full max-w-md bg-white border border-slate-200 rounded-2xl shadow-2xl overflow-hidden">
        {/* Top accent line */}
        <div className="h-1 w-full bg-navy-500" />

        <button
          onClick={dismiss}
          className="absolute top-4 right-4 text-slate-400 hover:text-navy-950 transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-8">
          {submitted ? (
            <div className="text-center py-4">
              <div className="w-14 h-14 rounded-full bg-green-50 border border-green-200 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-7 h-7 text-green-600" />
              </div>
              <h3 className="text-xl font-bold font-serif text-slate-900 mb-2">You're in!</h3>
              <p className="text-sm text-slate-500">
                Free Nifty &amp; Sensex analysis hits your inbox every morning.
              </p>
              <button
                onClick={dismiss}
                className="mt-6 text-xs text-slate-400 hover:text-slate-600 transition-colors underline underline-offset-2"
              >
                Continue reading
              </button>
            </div>
          ) : (
            <>
              {/* Icon */}
              <div className="flex justify-center mb-5">
                <div className="w-14 h-14 rounded-xl bg-navy-50 border border-navy-100 flex items-center justify-center">
                  <TrendingUp className="w-7 h-7 text-navy-500" />
                </div>
              </div>

              <h2 className="text-xl font-bold font-serif text-slate-900 text-center mb-2">
                Before you go —
              </h2>
              <p className="text-base text-slate-700 text-center mb-1">
                Get free Nifty &amp; Sensex analysis
              </p>
              <p className="text-sm text-slate-500 text-center mb-6">
                AI-powered market insights delivered to your inbox every morning. No spam. Unsubscribe anytime.
              </p>

              {/* Benefits */}
              <ul className="space-y-2.5 mb-6">
                {[
                  'Daily Nifty 50 & Bank Nifty outlook',
                  'Top stock ideas from AI analysis',
                  'Key levels to watch this week',
                ].map(item => (
                  <li key={item} className="flex items-center gap-2.5 text-sm text-slate-600">
                    <CheckCircle className="w-4 h-4 text-navy-500 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>

              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-4 py-3 focus-within:border-navy-500 transition-colors">
                  <Mail className="w-4 h-4 text-slate-400 shrink-0" />
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    className="bg-transparent text-slate-900 placeholder-slate-400 text-sm flex-1 outline-none"
                    autoFocus
                  />
                </div>

                {error && (
                  <p className="text-red-600 text-xs text-center">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-navy-500 hover:bg-navy-600 disabled:opacity-60 text-white font-semibold py-3 rounded-xl transition-all text-sm"
                >
                  {loading ? 'Subscribing…' : 'Send me free analysis →'}
                </button>
              </form>

              <p className="text-xs text-slate-400 text-center mt-4">
                Free forever · No credit card · Educational content only
              </p>

              <button
                onClick={dismiss}
                className="block w-full text-center text-xs text-slate-400 hover:text-slate-600 transition-colors mt-3"
              >
                No thanks, I don't want free analysis
              </button>
            </>
          )}
        </div>

        {/* Bottom border */}
        <div className="h-px w-full bg-slate-100" />
      </div>
    </div>
  );
};

export default ExitIntentPopup;
